import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp } from "../../utils/TextUtils.js";

function PaymentsByRoute() {
    const { periodId, routeId } = useParams();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState("");
    const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
    const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [periodInfo, setPeriodInfo] = useState(null);
    const [routeInfo, setRouteInfo] = useState(null);
    const token = localStorage.getItem("access_token");
    const navigate = useNavigate();
    const [downloading, setDownloading] = useState(false);
    const [excelDownloading, setExcelDownloading] = useState(false);
    const [cashExcelDownloading, setCashExcelDownloading] = useState(false);
    const userRole = localStorage.getItem("userRole");
    const canPayAll = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    const [bankPdfDownloading, setBankPdfDownloading] = useState(false);
    const [bankPaymentCount, setBankPaymentCount] = useState(0);

    const fetchBankPaymentCount = useCallback(async () => {
        try {
            let url = `${API_URL}/payment-periods/${periodId}/bank-payments/count`;
            if (routeId) {
                url = `${API_URL}/payment-periods/${periodId}/route/${routeId}/bank-payments/count`;
            }

            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setBankPaymentCount(data.count);
            }
        } catch (error) {
            console.error("Error fetching bank payment count:", error);
        }
    }, [periodId, routeId, token]);

    const handleDownloadBankPdf = async () => {
        try {
            let url = `${API_URL}/payment-periods/${periodId}/bank-payments/pdf`;

            if (routeId) {
                url = `${API_URL}/payment-periods/${periodId}/route/${routeId}/bank-payments/pdf`;
            }

            if (searchTerm.trim() !== "") {
                url += `?search=${encodeURIComponent(searchTerm)}`;
            }

            setBankPdfDownloading(true);

            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                const fileName = `bank-payments-${periodId}-${new Date().toISOString().slice(0, 10)}.pdf`;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                setNotification({
                    type: "success",
                    message: "Bank payments PDF downloaded successfully",
                    isVisible: true
                });
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download bank payments PDF');
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: error.message || "Failed to download bank payments PDF",
                isVisible: true
            });
            console.error("Bank PDF download error:", error);
        } finally {
            setBankPdfDownloading(false);
        }
    };

    useEffect(() => {
        fetchBankPaymentCount();
    }, [fetchBankPaymentCount]);

    useEffect(() => {
        if (payments.length > 0) {
            fetchBankPaymentCount();
        }
    }, [payments.length, fetchBankPaymentCount]);

    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/payment-periods/${periodId}/payments?page=${currentPage}`;

            // Add route filter if routeId exists
            if (routeId) {
                url = `${API_URL}/payment-periods/${periodId}/route/${routeId}?page=${currentPage}`;
            }

            // Add search term
            if (searchTerm.trim() !== "") {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setPayments(data.data || []);
                setCurrentPage(data.current_page || 1);
                setTotalPages(data.last_page || 1);
                setTotalItems(data.total || 0);
                setPeriodInfo({
                    name: data.period_name,
                    status: data.period_status,
                    routeName: data.route_name
                });

                // Show notification if period is closed
                if (data.period_status === 'Closed') {
                    setNotification({
                        type: "danger",
                        message: "Payments cannot be processed as this period is closed",
                        isVisible: true
                    });
                }

                // Get route info if routeId exists
                if (routeId && !routeInfo) {
                    const routeResponse = await fetch(`${API_URL}/collection-routes/${routeId}`, {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                        },
                    });
                    if (routeResponse.ok) {
                        const routeData = await routeResponse.json();
                        setRouteInfo(routeData.data);
                    }
                }
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch payments");
            }
        } catch (error) {
            setError("An error occurred while fetching payments.");
            setNotification({
                type: "danger",
                message: "Failed to load payment data",
                isVisible: true
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, periodId, routeId, token, navigate, routeInfo]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const closeNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    const markAllAsPaid = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/payment-periods/${periodId}/mark-route-paid/${routeId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setNotification({
                    type: "success",
                    message: data.message || "Payments marked as paid successfully",
                    isVisible: true
                });
                // Refresh the payments list
                await fetchPayments();
            } else if (response.status === 400) {
                const errorData = await response.json();
                setNotification({
                    type: "danger",
                    message: errorData.message || "Cannot mark payments as paid for a closed period",
                    isVisible: true
                });
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to mark payments as paid");
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: "An error occurred while marking payments as paid",
                isVisible: true
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
        }
    };

    // Update your handleConfirmation function
    const handleConfirmation = async () => {
        try {
            switch (confirmAction) {
                case "markAsPaid":
                    await markAllAsPaid();
                    break;
                default:
                    break;
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: "There was an error handling confirmation",
                isVisible: true
            });
        }
    }

    // Update your handleConfirmPaymentStatus function
    const handleConfirmPaymentStatus = () => {
        if (periodInfo?.status === 'Closed') {
            setNotification({
                type: "danger",
                message: "Cannot mark payments as paid for a closed period",
                isVisible: true
            });
            return;
        }

        setConfirmAction("markAsPaid");
        setConfirmDialogTitle("Confirm Mark All As Paid");
        setConfirmDialogMessage(`Are you sure you want to mark all unpaid payments in route "${periodInfo?.routeName}" as paid? This action cannot be undone.`);
        setConfirmDialogOpen(true);
    };

    const handleDownloadPdf = async () => {
        try {
            const token = localStorage.getItem("access_token");
            let url = `${API_URL}/payment-periods/${periodId}/payments/pdf`;

            if (routeId) {
                url = `${API_URL}/payment-periods/${periodId}/route/${routeId}/pdf`;
            }

            if (searchTerm.trim() !== "") {
                url += `?search=${encodeURIComponent(searchTerm)}`;
            }
            setDownloading(true);
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `payments-${periodId}-${new Date().toISOString().slice(0, 10)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                throw new Error('Failed to download PDF');
            }
            setDownloading(false);
        } catch (error) {
            setNotification({
                type: "danger",
                message: "Failed to download PDF",
                isVisible: true
            });
            console.error("PDF download error:", error);
            setDownloading(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            let url = `${API_URL}/payment-periods/${periodId}/mpesa-excel`;

            if (routeId) {
                url = `${API_URL}/payment-periods/${periodId}/route/${routeId}/mpesa-excel`;
            }

            setExcelDownloading(true);

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                // Generate filename with date
                const date = new Date().toISOString().slice(0, 10);
                const fileName = routeId
                    ? `mpesa-payments-${periodInfo?.name}-${periodInfo?.routeName}-${date}.xlsx`
                    : `mpesa-payments-${periodInfo?.name}-${date}.xlsx`;

                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                setNotification({
                    type: "success",
                    message: "Excel file downloaded successfully",
                    isVisible: true
                });
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download Excel file');
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: error.message || "Failed to download Excel file",
                isVisible: true
            });
            console.error("Excel download error:", error);
        } finally {
            setExcelDownloading(false);
        }
    };

    const handleDownloadCashExcel = async () => {
    try {
        let url = `${API_URL}/payment-periods/${periodId}/cash-excel`;

        if (routeId) {
            url = `${API_URL}/payment-periods/${periodId}/route/${routeId}/cash-excel`;
        }

        setCashExcelDownloading(true);

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (response.ok) {
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            const date = new Date().toISOString().slice(0, 10);
            const fileName = routeId
                ? `cash-payments-${periodInfo?.name}-${periodInfo?.routeName}-${date}.xlsx`
                : `cash-payments-${periodInfo?.name}-${date}.xlsx`;

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setNotification({
                type: "success",
                message: "Cash Excel file downloaded successfully",
                isVisible: true
            });
        } else if (response.status === 401) {
            navigate("/logout");
        } else {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to download Cash Excel file');
        }
    } catch (error) {
        setNotification({
            type: "danger",
            message: error.message || "Failed to download Cash Excel file",
            isVisible: true
        });
        console.error("Cash Excel download error:", error);
    } finally {
        setCashExcelDownloading(false);
    }
};

    // Count Mpesa payments
    const mpesaPaymentCount = payments.filter(payment =>
        payment.farmer?.account_type === 'Mpesa'
    ).length;

    return (
        <div className="container">
            <main className="box">
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <div>
                                <h1 className="title is-4">
                                    {periodInfo?.name || 'Payments'}
                                    {periodInfo?.status && (
                                        <span className={`tag ml-2 ${periodInfo.status === 'Closed' ? 'is-danger' : 'is-success'}`}>
                                            {periodInfo.status}
                                        </span>
                                    )}
                                </h1>
                                {periodInfo?.routeName && (
                                    <h2 className="subtitle is-5 mt-2">
                                        Route: <strong>{periodInfo.routeName}</strong>
                                    </h2>
                                )}

                            </div>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <Link
                                to={`/dashboard/payment-periods/${periodId}/summary`}
                                className="button is-info"
                            >
                                <span className="icon">
                                    <i className="fas fa-arrow-left"></i>
                                </span>
                                <span>Back</span>
                            </Link>
                        </div>
                    </div>
                </nav>

                {notification.isVisible && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-primary"
                    title={confirmDialogTitle}
                    message={confirmDialogMessage}
                    onConfirm={handleConfirmation}
                    onCancel={() => setConfirmDialogOpen(false)}
                />

                <div className="field">
                    <label className="label">Search Farmers</label>
                    <div className="control has-icons-left">
                        <input
                            className="input"
                            type="text"
                            placeholder="Search by name or number..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className="icon is-small is-left">
                            <i className="fas fa-search"></i>
                        </span>
                    </div>
                </div>

                <div className="box has-background-light">
                    <div className="level">
                        <div className="level-left">
                            <div className="level-item">
                                <p>
                                    Showing {payments.length} of {totalItems} payments
                                    {searchTerm && ` matching "${searchTerm}"`}
                                </p>
                            </div>
                        </div>
                        <div className="level-right">
                            <div className="level-item">
                                <button
                                    className="button is-small is-info mr-2"
                                    onClick={fetchPayments}
                                    disabled={loading}
                                >
                                    <span className="icon">
                                        <i className={`fas ${loading ? 'fa-spinner fa-pulse' : 'fa-sync-alt'}`}></i>
                                    </span>
                                    <span>{loading ? 'Loading...' : 'Refresh'}</span>
                                </button>

                                {routeId && (
                                    <>
                                        {canPayAll(userRole) && (
                                            <button
                                                className="button is-small is-success mr-2"
                                                onClick={handleConfirmPaymentStatus}
                                                disabled={loading || periodInfo?.status === 'Closed'}
                                            >
                                                <span className="icon">
                                                    <i className="fas fa-check-circle"></i>
                                                </span>
                                                <span>Mark All As Paid</span>
                                            </button>
                                        )}
                                    </>
                                )}

                                <button
                                    className={`button is-small is-primary mr-2 ${downloading ? 'is-loading' : ''}`}
                                    onClick={handleDownloadPdf}
                                    disabled={loading}
                                >
                                    <span className="icon">
                                        <i className="fas fa-file-pdf"></i>
                                    </span>
                                    <span>All Payments PDF</span>
                                </button>

                                {/* New Bank Payments PDF Button */}
                                <button
                                    className={`button is-small is-warning mr-2 ${bankPdfDownloading ? 'is-loading' : ''}`}
                                    onClick={handleDownloadBankPdf}
                                    disabled={loading || bankPaymentCount === 0}
                                    title={bankPaymentCount > 0 ? `Download bank & SACCO payments (${bankPaymentCount} payments)` : "No bank payments available"}
                                >
                                    <span className="icon">
                                        <i className="fas fa-university"></i>
                                    </span>
                                    <span>Banks & SACCOs PDF</span>
                                    {bankPaymentCount > 0 && (
                                        <span className="tag is-light ml-1">{bankPaymentCount}</span>
                                    )}
                                </button>

                                <button
                                    className={`button is-small is-success ${excelDownloading ? 'is-loading' : ''}`}
                                    onClick={handleDownloadExcel}
                                    disabled={loading}
                                    title="Download Mpesa payments as Excel"
                                >
                                    <span className="icon">
                                        <i className="fas fa-file-excel"></i>
                                    </span>
                                    <span>Mpesa Excel</span>
                                </button>

                                <button
                                    className={`button is-small is-link ml-2 ${cashExcelDownloading ? 'is-loading' : ''}`}
                                    onClick={handleDownloadCashExcel}
                                    disabled={loading}
                                    title="Download Cash payments as Excel"
                                >
                                    <span className="icon">
                                        <i className="fas fa-money-bill-wave"></i>
                                    </span>
                                    <span>Cash Excel</span>
                                </button>
                                
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="has-text-centered">
                        <progress className="progress is-small is-primary" max="100">Loading</progress>
                        <p>Loading payments...</p>
                    </div>
                ) : error ? (
                    <div className="notification is-danger">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table is-striped is-fullwidth is-hoverable">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Farmer</th>
                                        <th>Account Type</th>
                                        <th className="has-text-right">Milk Value</th>
                                        <th className="has-text-right">Debt</th>
                                        <th className="has-text-right">Arrears</th>
                                        <th className="has-text-right">Total Income</th>
                                        <th className="has-text-right">Amount To Pay</th>
                                        <th>Status</th>
                                        <th className="has-text-right">Amount Paid</th>
                                        <th>Paid At</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.length > 0 ? (
                                        payments.map((payment, index) => (
                                            <tr key={payment.id}>
                                                <td>{((currentPage - 1) * 30) + index + 1}</td>
                                                <td>
                                                    {payment.farmer ? (
                                                        <Link
                                                            to={`/dashboard/farmers/${payment.farmer.id}/details`}
                                                            className="primary-text-color"
                                                        >
                                                            {payment.farmer.first_name} {payment.farmer.middle_name} {payment.farmer.last_name}
                                                            <br />
                                                            <small className="has-text-grey">#{payment.farmer.farmer_number}</small>
                                                        </Link>
                                                    ) : 'N/A'}
                                                </td>
                                                <td>
                                                    {payment.farmer?.account_type === 'Mpesa' ? (
                                                        <span className="tag is-info">
                                                            <span className="icon-text">
                                                                <span className="icon is-small">
                                                                    <i className="fas fa-mobile-alt"></i>
                                                                </span>
                                                                <span>Mpesa</span>
                                                            </span>
                                                        </span>
                                                    ) : payment.farmer?.account_type === 'Bank' ? (
                                                        <span className="tag is-primary">Bank</span>
                                                    ) : (
                                                        <span className="tag is-light">Cash</span>
                                                    )}
                                                </td>
                                                <td className="has-text-right">{payment.total_milk_price}</td>
                                                <td className="has-text-right">{payment.total_debt}</td>
                                                <td className="has-text-right">{payment.arrears}</td>
                                                <td className="has-text-right">{payment.total_income}</td>
                                                <td className="has-text-right has-text-weight-bold">
                                                    {payment.amount_to_pay}
                                                </td>
                                                <td>
                                                    {payment.status === 'Paid' ? (
                                                        <span className="tag is-success">Paid</span>
                                                    ) : payment.status === 'Partially Paid' ? (
                                                        <span className="tag is-warning">Partial</span>
                                                    ) : (
                                                        <span className="tag is-danger">Unpaid</span>
                                                    )}
                                                </td>
                                                <td className="has-text-right">{payment.amount_paid}</td>
                                                <td>
                                                    {payment.paid_at ? formatTimestamp(payment.paid_at) : 'N/A'}
                                                </td>
                                                <td>
                                                    <Link
                                                        to={`/dashboard/farmers/${payment.farmer.id}/details`}
                                                        className="button is-small is-info"
                                                        title="View Details"
                                                        disabled={periodInfo?.status === 'Closed'}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-eye"></i>
                                                        </span>
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="has-text-centered">
                                                {searchTerm ?
                                                    `No payments found matching "${searchTerm}"` :
                                                    'No payments found'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                                <button
                                    className="pagination-previous"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1 || loading}
                                >
                                    Previous
                                </button>
                                <button
                                    className="pagination-next"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages || loading}
                                >
                                    Next
                                </button>
                                <ul className="pagination-list">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <li key={pageNum}>
                                                <button
                                                    className={`pagination-link ${currentPage === pageNum ? 'is-current' : ''}`}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    disabled={loading}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

export default PaymentsByRoute;